// src/components/ProductCard.jsx
import { useState } from "react";
import { ProductBadge } from "./UI";
import { useMobile } from "../hooks/useMobile";
import { withCloudinaryAutoFormatQualityList, withCloudinaryProductCardOptimizationList } from "../utils/cloudinary";

// ─── Share helpers ────────────────────────────────────────────────────────────
function buildShareData(design, siteUrl = process.env.REACT_APP_SITE_URL || window.location.origin) {
  const url     = `${siteUrl}/products/${design.id || "all"}`;
  const image   = design.images?.[0] || design.imageUrl || "";
  const details = [
    design.price   && `💰 Price: ${design.price}`,
  ].filter(Boolean).join("\n");

  const whatsappText =
    `👗 *Design*\n\n${details}\n\n🛍️ View more designs: ${url}`;

  const instagramCaption =
    `✨ New Design ✨\n\n${details}\n\n` +
    `Shop our full collection 👉 ${url}\n\n` +
    `#NilamSynthetics #KurtiCollection #LadiesKurti #AhmedabadFashion #IndianWear #EthnicWear #KurtisOfInstagram #NewArrival`;

  return { url, image, whatsappText, instagramCaption };
}

// Share via native Web Share API (mobile) or fallback
async function shareProduct(design, method, onCopied) {
  const { url, whatsappText, instagramCaption } = buildShareData(design);

  if (method === "native" && navigator.share) {
    try {
      await navigator.share({
        title: "Nilam Synthetics Design",
        text:  `Check out this design from Nilam Synthetics!\n${design.price}`,
        url,
      });
    } catch (e) { /* user cancelled */ }
    return;
  }

  if (method === "whatsapp") {
    window.open(`https://wa.me/?text=${encodeURIComponent(whatsappText)}`, "_blank");
    return;
  }

  if (method === "instagram") {
    // Instagram doesn't support URL sharing from web — copy caption to clipboard
    try {
      await navigator.clipboard.writeText(instagramCaption);
      onCopied("Caption copied! Open Instagram and paste it.");
    } catch {
      onCopied("Copy this caption manually.");
    }
    return;
  }

  if (method === "copy") {
    try {
      await navigator.clipboard.writeText(url);
      onCopied("Link copied!");
    } catch {
      onCopied("Could not copy link.");
    }
  }
}

// ─── Share Button Strip ───────────────────────────────────────────────────────
function ShareStrip({ design, compact }) {
  const [copied, setCopied]   = useState("");
  const hasNativeShare = !!navigator.share;

  const onCopied = (msg) => { setCopied(msg); setTimeout(() => setCopied(""), 2800); };

  if (compact) {
    // Card hover — small icon row
    return (
      <div onClick={e => e.stopPropagation()} className="product-share-strip product-share-strip--compact">
        {copied ? (
          <span className="product-share-strip__copied product-share-strip__copied--compact">{copied}</span>
        ) : (
          <>
            {hasNativeShare && (
              <ShareBtn icon={<NativeShareIcon />} label="Share" variant="native"
                onClick={() => shareProduct(design, "native", onCopied)} />
            )}
            <ShareBtn icon={<WhatsAppIcon />} label="WhatsApp" variant="whatsapp"
              onClick={() => shareProduct(design, "whatsapp", onCopied)} />
            <ShareBtn icon={<InstagramIcon />} label="Instagram" variant="instagram"
              onClick={() => shareProduct(design, "instagram", onCopied)} />
            <ShareBtn icon={<CopyIcon />} label="Copy" variant="copy"
              onClick={() => shareProduct(design, "copy", onCopied)} />
          </>
        )}
      </div>
    );
  }

  // Modal — full row with labels
  return (
    <div onClick={e => e.stopPropagation()}>
      {copied && (
        <div className="product-share-strip__copied">
          ✓ {copied}
        </div>
      )}
      <div className="product-share-strip product-share-strip--full">
        {hasNativeShare && (
          <ShareBtnFull icon={<NativeShareIcon color="#fff" />} label="Share" variant="native"
            onClick={() => shareProduct(design, "native", onCopied)} />
        )}
        <ShareBtnFull icon={<WhatsAppIcon color="#fff" />} label="WhatsApp" variant="whatsapp"
          onClick={() => shareProduct(design, "whatsapp", onCopied)} />
        <ShareBtnFull icon={<InstagramIcon color="#fff" />} label="Instagram" variant="instagram"
          onClick={() => shareProduct(design, "instagram", onCopied)} />
        <ShareBtnFull icon={<CopyIcon color="#fff" />} label="Copy Link" variant="copy"
          onClick={() => shareProduct(design, "copy", onCopied)} />
      </div>
    </div>
  );
}

function ShareBtn({ icon, label, variant, onClick }) {
  return (
    <button onClick={onClick} title={label} className={`product-share-btn product-share-btn--${variant}`}>
      {icon}
      <span>{label}</span>
    </button>
  );
}

function ShareBtnFull({ icon, label, variant, onClick }) {
  return (
    <button onClick={onClick} className={`product-share-btn-full product-share-btn-full--${variant}`}>
      {icon}
      <span>{label}</span>
    </button>
  );
}

// ─── SVG Icons ────────────────────────────────────────────────────────────────
const WhatsAppIcon = ({ color = "#25D366", size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const InstagramIcon = ({ color = "#E1306C", size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
  </svg>
);

const NativeShareIcon = ({ color = "#555", size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
  </svg>
);

const CopyIcon = ({ color = "#7A6256", size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
  </svg>
);

const ViewIcon = ({ color = "#E06B4F", size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
    <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
  </svg>
);

function PageRedirectLoader() {
  return (
    <div role="status" aria-live="polite" className="product-redirect-loader">
      <video src="/brand-intro.mp4" autoPlay loop muted playsInline aria-label="Loading" className="product-redirect-loader__image" />
      <div className="product-redirect-loader__text">
        Opening product page...
      </div>
    </div>
  );
}

function ImageNavButton({ direction, onClick, dark = true }) {
  const isPrev = direction === "prev";
  const classes = [
    "product-image-nav",
    `product-image-nav--${direction}`,
    dark ? "product-image-nav--dark" : "product-image-nav--light",
  ].join(" ");

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={isPrev ? "Show previous image" : "Show next image"}
      className={classes}
    >
      <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path
          d={isPrev ? "M12.5 4.5 7 10l5.5 5.5" : "M7.5 4.5 13 10l-5.5 5.5"}
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

// ─── Product Card ─────────────────────────────────────────────────────────────
export function ProductCard({ design, onClick, whatsapp }) {
  const [imgIndex,    setImgIndex]    = useState(0);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const rawImages = design.images?.length ? design.images : design.imageUrl ? [design.imageUrl] : [];
  const images = withCloudinaryProductCardOptimizationList(rawImages);
  const siteUrl = process.env.REACT_APP_SITE_URL || window.location.origin;
  const url     = `${siteUrl}/products/${design.id || "all"}`;

  const whatsappEnquireLink = whatsapp
    ? `https://wa.me/${whatsapp.replace(/\D/g,"")}?text=Hi! I'm interested in this design. Please share more details. ${url}`
    : null;

  const showPrevImage = (e) => {
    e.stopPropagation();
    setImgIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const showNextImage = (e) => {
    e.stopPropagation();
    setImgIndex((prev) => (prev + 1) % images.length);
  };

  const handleViewProductPage = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (isRedirecting) return;

    const destination = `/products/${design.id}`;
    setIsRedirecting(true);

    setTimeout(() => {
      window.location.assign(destination);
    }, 40);
  };

  return (
    <>
      <article className="product-card">
        {/* Image area */}
        <div onClick={() => onClick(design)} className="product-card__image-area">
          {images.length > 0
            ? <img src={images[imgIndex]} alt="Design image" loading="lazy" className="product-card__image" />
            : <div className="product-card__placeholder">
                <div className="product-card__placeholder-icon">👗</div>
                <span className="product-card__placeholder-text">Photo coming soon</span>
              </div>
          }
          {/* Dots */}
          {images.length > 1 && (
            <div className="product-card__dots">
              {images.map((_, i) => (
                <div
                  key={i}
                  onClick={e => { e.stopPropagation(); setImgIndex(i); }}
                  className={`product-card__dot${i === imgIndex ? " product-card__dot--active" : ""}`}
                />
              ))}
            </div>
          )}
          {/* Image navigation */}
          {images.length > 1 && (
            <>
              <ImageNavButton direction="prev" onClick={showPrevImage} />
              <ImageNavButton direction="next" onClick={showNextImage} />
            </>
          )}
          {/* Badge */}
          <div className="product-card__badge">
            {design.badge && <ProductBadge type={design.badge} />}
          </div>
          <div className="product-card__image-gradient" />
        </div>

        {/* Info */}
        <div onClick={() => onClick(design)} className="product-card__info">
          <div className="product-card__header">
            {design.fabric && (
              <span className="product-card__fabric-chip">
                {design.fabric}
              </span>
            )}
          </div>
          <div className="product-card__meta-list">
            {[["Colors", design.colors], ["Combo Type", `Set of ${design.setPieces || 4} ${design.setType || 'colors'}`], ["Fabric", design.fabric], ["Sizes", design.sizes]].map(([k, v]) =>
              v ? <div key={k} className="product-card__meta"><span>{k}: </span>{v}</div> : null
            )}
          </div>
        </div>
        <div className="product-card__price-wrap">
          {[["Price", design.price + '/piece']].map(([k, v]) =>
            v ? <div key={k} className="product-card__price"><span>{k}: </span>{v}</div> : null
          )}
        </div>
        <div className="product-card__actions">
          {/* View Product Page */}
          <a href={`/products/${design.id}`}
            onClick={handleViewProductPage}
            aria-busy={isRedirecting}
            className={`product-card__view-link${isRedirecting ? " product-card__view-link--loading" : ""}`}>
            {isRedirecting ? (
              <>
                <video src="/brand-intro.mp4" autoPlay loop muted playsInline aria-label="Loading" className="product-card__view-loader" /> Opening...
              </>
            ) : (
              <>
                <ViewIcon /> View Product Page
              </>
            )}
          </a>

          {/* WhatsApp enquiry */}
          {whatsappEnquireLink && (
            <a href={whatsappEnquireLink} target="_blank" rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              className="product-card__whatsapp-link whatsapp-enquire-btn">
              <WhatsAppIcon color="#fff" size={13} /> Enquire on WhatsApp
            </a>
          )}
        </div>
      </article>
      {isRedirecting && <PageRedirectLoader />}
    </>
  );
}

// ─── Product Detail Modal ─────────────────────────────────────────────────────
export function ProductModal({ design, onClose, whatsapp }) {
  const [imgIndex, setImgIndex] = useState(0);
  const isMobile           = useMobile();
  if (!design) return null;

  const rawImages = design.images?.length ? design.images : design.imageUrl ? [design.imageUrl] : [];
  const images = withCloudinaryAutoFormatQualityList(rawImages);
  const showPrevImage = (e) => {
    e.stopPropagation();
    setImgIndex((prev) => (prev - 1 + images.length) % images.length);
  };
  const showNextImage = (e) => {
    e.stopPropagation();
    setImgIndex((prev) => (prev + 1) % images.length);
  };
  const whatsappEnquireLink = whatsapp
    ? `https://wa.me/${whatsapp.replace(/\D/g,"")}?text=Hi! I'm interested in this design. Please share details and availability.`
    : null;

  const fields = [
    ["Colors", design.colors], ["Combo Type", `Set of ${design.setPieces || 4} ${design.setType || 'colors'}`], ["Fabric", design.fabric], ["Work", design.work],
    ["Kurti Length", design.kurtilength], ["Bottom Length", design.bottomLength],
    ["Dupatta", design.dupattaDetails], ["Sizes", design.sizes],
  ].filter(([, v]) => v);

  return (
    <div onClick={onClose} className="product-modal">
      <div onClick={e => e.stopPropagation()} className="product-modal__panel">
        <div className="product-modal__accent" />

        <div className="product-modal__body">
          {/* Image */}
          <div className="product-modal__image-area">
            {images.length > 0
              ? <img src={images[imgIndex]} alt="Design image" className="product-modal__image" />
              : <div className="product-modal__placeholder">👗</div>
            }
            {images.length > 1 && (
              <>
                <ImageNavButton direction="prev" onClick={showPrevImage} />
                <ImageNavButton direction="next" onClick={showNextImage} />
              </>
            )}
            {images.length > 1 && (
              <div className="product-modal__thumbs">
                {images.map((img, i) => (
                  <div
                    key={i}
                    onClick={e => { e.stopPropagation(); setImgIndex(i); }}
                    className={`product-modal__thumb${i === imgIndex ? " product-modal__thumb--active" : ""}`}
                  >
                    <img src={img} alt="" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="product-modal__details">
            <div className="product-modal__badge-row">
              {design.badge && <ProductBadge type={design.badge} />}
            </div>
            <div className="product-modal__price">
              {design.price}
            </div>

            {/* Spec grid */}
            <div className="product-modal__spec-list">
              {fields.map(([k, v]) => (
                <div key={k} className="product-modal__spec">
                  <div className="product-modal__spec-label">{k}</div>
                  <div className="product-modal__spec-value">{v}</div>
                </div>
              ))}
            </div>

            {/* Share buttons */}
            {!isMobile && (
              <div className="product-modal__share">
                <div className="product-modal__share-title">Share</div>
                <ShareStrip design={design} compact={false} />
              </div>
            )}

            {/* WhatsApp enquire */}
            {whatsappEnquireLink && (
              <a href={whatsappEnquireLink} target="_blank" rel="noopener noreferrer" className="product-modal__whatsapp-link whatsapp-enquire-btn">
                <WhatsAppIcon color="#fff" size={15} /> Enquire on WhatsApp
              </a>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="product-modal__footer">
          <a href={`/products/${design.id}`} className="product-modal__view-link">
            <ViewIcon /> View Product Page
          </a>
          <button onClick={onClose} className="product-modal__close">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
